import clsxm from "immigram-ui-cmon/src/lib/clsxm";
import { ReactElement, useEffect, useRef, useState } from "react";

import { Icon24Check } from "../Icons";

interface DropdownProps {
  options: DropdownOption[];
  onSelect?: (option: DropdownOption) => void;
  nowrap?: boolean;
  small?: boolean;
  className?: string;
  position?: "top" | "bottom" | "top-left" | "bottom-left" | "bottom-right";
}

export interface DropdownOption {
  title: React.ReactNode;
  value: string;
  before?: ReactElement;
  disabled?: boolean;
  checked?: boolean;
  nowrap?: boolean;
  group?: string;
  groupValue?: string;
  subTitle?: React.ReactNode;
}

const TOP_OFFSET = 4;
const LEFT_OFFSET = 4;

const Dropdown = (props: DropdownProps) => {
  const { options = [], nowrap = true } = props;
  const [height, setHeight] = useState(0);
  const [width, setWidth] = useState(0);
  const rootGroup = [];
  const groups: { [key: string]: DropdownOption[] } = {};
  const rootRef = useRef<HTMLDivElement | null>(null);

  for (const option of options) {
    if (!option.group) {
      rootGroup.push(option);
      continue;
    }

    if (!groups[option.group]) {
      groups[option.group] = [];
    }

    groups[option.group].push({ ...option });
  }

  useEffect(() => {
    if (rootRef.current) {
      setHeight(rootRef.current.offsetHeight);
      setWidth(rootRef.current.offsetWidth);
    }
  }, [rootRef, setHeight, setWidth, props]);

  function renderOption(option: DropdownOption, index: number): ReactElement {
    return (
      <div
        key={option.value + option.group}
        className={clsxm(
          "delay-50 group h-11 w-auto rounded-b-none transition duration-150 ease-in-out first:mt-2 last:mb-2 last:rounded-t-none",
          !option.disabled && "cursor-pointer hover:bg-black-100"
        )}
      >
        {index > 0 && (
          <div
            className={clsxm(
              "group:hover:border-b-0 ml-4 w-auto border-b border-solid border-black-100"
            )}
          />
        )}

        <div
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            !option.disabled && props.onSelect && props.onSelect(option);
          }}
          className="py-2.5"
        >
          <div
            className={clsxm(
              "ml-4 mr-8",
              nowrap ? "overflow-hidden text-ellipsis whitespace-nowrap" : ""
            )}
          >
            {option.before && (
              <span className="relative mr-1 h-6 text-black-500">
                {option.before}
              </span>
            )}
            <span className="align-top text-black-900">{option.title}</span>
            {option.checked && (
              <Icon24Check
                active={true}
                className="absolute right-4 inline-block text-black-900"
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      className={clsxm(
        props.small ? "top-72t right-2 w-52" : "w-full",
        "absolute z-50 flex-auto overflow-hidden rounded-2xl border border-gray-150 bg-white-900 text-base shadow-lg",
        props.className ?? ""
      )}
      style={
        {
          top: { top: `-${height + TOP_OFFSET}px` },
          bottom: {},
          "top-left": {
            top: `-${height + TOP_OFFSET}px`,
            left: `-${width + LEFT_OFFSET}px`,
          },
          "bottom-left": {
            top: TOP_OFFSET,
            right: 0,
          },
          "bottom-right": {
            top: TOP_OFFSET,
            left: 0,
          },
        }[props.position ?? "bottom"]
      }
    >
      {rootGroup.map(renderOption)}
      {Object.keys(groups).map((group) => (
        <div className="last:mb-4" key={group}>
          <div className="ml-0 bg-black-50 px-4 py-1 text-xs font-semibold text-black-900 first:mt-2">
            {group}
          </div>
          <div className="my-[-8px]">{groups[group].map(renderOption)}</div>
        </div>
      ))}
    </div>
  );
};

export default Dropdown;
